import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import type { DialogOptions } from '@adornis/dialog/x-plain-dialog.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import './cr-button.js';
import './fonts.js';

/**
 * @customElement cr-dialog
 *
 * @prop heading: string. The string displayed in the header line to name the dialog.
 * @prop background: ColorType, default=white. !deprecated! The Background of the dialog. This should not be needed in the future but the design is slowly advancing so we need this for legacy reasons.
 * @prop fullscreen: boolean, default=false. Whether to open the dialog in fullscreen. When you just dont have enough space.
 * @prop display: string, default=flex. The display of the inner container. Flex should be enough for most cases. But in the case of a really large component you probably need overflow-x. In this case you should use 'inline-flex' here, so the scrolling behavior is stable. Fullscreen will help here too.
 *
 * The Chancenwerk spcific wrapper for the dialog modal. It provides more specified behavior like sticky header and footer you can easily override with 'headingContent()' and 'footerContent()'.
 * It just gives you a more consistent styling for different use cases. You can override its behavior like you would with a normal XDialog.
 */
@customElement('cr-dialog')
export class ChromiumDialog<T> extends XDialog<T> {
  static override get element_name() {
    return 'cr-dialog';
  }

  @property() heading?: string;
  @property({ attribute: false }) background = this.colors.white;
  @property({ attribute: false }) fullscreen = false;
  @property({ attribute: true, type: String, reflect: true }) display = 'flex';

  override render() {
    if (this.dialogShowPopupContent) return this.dialogShowPopupContent;
    if (this.heading)
      return html`
        <cr-flex space="md" margin="md" ${css({ display: this.display })}>
          <cr-flex
            ${css({
              position: 'sticky',
              top: 0,
              left: 0,
              background: this.background,
              zIndex: 3,
              marginLeft: `-${this.spacing.md.number}px`,
              marginRight: `-${this.spacing.md.number}px`,
              paddingLeft: `${this.spacing.md.number}px`,
              paddingRight: `${this.spacing.md.number}px`,
              paddingBottom: this.spacing.sm,
              paddingTop: this.spacing.sm,
              alignSelf: this.display === 'inline-block' ? 'flex-start' : 'unset',

              width: this.display === 'inline-block' ? '100%' : 'unset',
              maxWidth: '100vw',
              borderRadius: `${this.fullscreen ? '0' : this.sizes.borderRadius} ${
                this.fullscreen ? '0' : this.sizes.borderRadius
              } 0 0`,
            })}
            horizontal
            space-between
            crossaxis-center
          >
            ${this.headingContent()}
          </cr-flex>

          ${this.content()}
          ${this.footerContent()
            ? html`
                <cr-flex
                  padding="md"
                  space="md"
                  ${css({
                    position: 'sticky',
                    bottom: 0,
                    left: 0,
                    background: this.background,
                    zIndex: 3,
                    marginLeft: `-${this.spacing.md.number}px`,
                    marginRight: `-${this.spacing.md.number}px`,
                    paddingLeft: `${this.spacing.md.number}px`,
                    paddingRight: `${this.spacing.md.number}px`,
                    paddingBottom: this.spacing.sm,
                    paddingTop: this.spacing.sm,
                    alignSelf: this.display === 'inline-block' ? 'flex-start' : 'unset',

                    width: this.display === 'inline-block' ? '100%' : 'unset',

                    maxWidth: '100vw',

                    borderRadius: `0 0 ${this.fullscreen ? '0' : this.sizes.borderRadius} ${
                      this.fullscreen ? '0' : this.sizes.borderRadius
                    }`,
                  })}
                >
                  ${this.footerContent()}
                </cr-flex>
              `
            : nothing}
        </cr-flex>
      `;
    return this.content();
  }

  protected content(): Renderable {
    return nothing;
  }

  protected footerContent(): Renderable {
    return nothing;
  }

  protected headingContent() {
    if (!this.heading) return nothing;
    return html`
      <cr-h3> ${this.heading} </cr-h3>
      <cr-button ${css({ color: this.colors.tone.error })} big mode="icon" icon="close" @click=${() => this.cancel()}>
      </cr-button>
    `;
  }

  public override getContentWrapper(options: DialogOptions) {
    const wrapperElem = super.getContentWrapper(options as Required<DialogOptions>);

    if (!options.draggable && this.fullscreen) {
      wrapperElem.style.setProperty('min-width', '100vw');
      wrapperElem.style.setProperty('max-width', '100vw');
      wrapperElem.style.setProperty('min-height', '100vh');
      wrapperElem.style.setProperty('max-height', '100vh');
      wrapperElem.style.setProperty('border-radius', '0px');
    }

    return wrapperElem;
  }

  public override getInnerWrapper(options: DialogOptions) {
    const wrapperElem = super.getInnerWrapper(options as Required<DialogOptions>);
    wrapperElem.style.setProperty('background', this.background);

    if (!options.draggable && this.fullscreen) {
      wrapperElem.style.setProperty('min-width', '100vw');
      wrapperElem.style.setProperty('max-width', '100vw');
      wrapperElem.style.setProperty('min-height', '100vh');
      wrapperElem.style.setProperty('max-height', '100vh');
      wrapperElem.style.setProperty('border-radius', '0px');
    }

    return wrapperElem;
  }

  /**
   * Task should return a number:
   * 1 if the entity got created
   * 0 if the entity got saved
   */
  public static async saveGuard<T = void>(entityName: string, task: () => Promise<number>) {
    await this.guard(task, {
      success: (result: number) =>
        ChromiumSnackbar.show(entityName + ' erfolgreich ' + (result ? 'angelegt' : 'gespeichert') + '!'),
    });
  }
}
